<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <InputLabel
    :label="label"
    :tooltip="tooltip"
    :is-error="isError"
    :warning="warning"
    :class="props.class"
  >
    <div class="relative flex w-full items-stretch justify-start">
      <input
        v-if="type !== 'number'"
        ref="input"
        :value="modelValue"
        :type="type"
        :placeholder="placeholder"
        :readonly="readonly"
        :disabled="disabled"
        :class="inputCva({ size, variant })"
        v-bind="$attrs"
        :data-error="isError ? 'true' : undefined"
        @input="
          (event) => {
            $emit('update:modelValue', input?.value as ValueType);
            $emit('input', event as InputEvent);
          }
        "
      />
      <InputNumber
        v-else
        v-model="numberValue"
        :locale="locale as string"
        :class="inputCva({ size, variant })"
        :min="min"
        :max="max"
        :min-fraction-digits="minFractionDigits"
        :max-fraction-digits="maxFractionDigits"
        :placeholder="placeholder"
        :use-grouping="useGrouping"
        :readonly="readonly"
        :disabled="disabled"
        v-bind="$attrs"
        :mode="minFractionDigits ? 'decimal' : undefined"
        input-class="min-w-0"
        @input="
          (event) => {
            getInputNumberValue(event.value);
            $emit('input', event);
          }
        "
        @blur="
          // Fix for older Android versions
          nextTick(() => {
            getInputNumberValue(numberValue);
          })
        "
      />
      <div
        v-if="adornment"
        class="absolute right-0 top-0 flex h-full items-center rounded-r-sm"
        :class="{
          'bg-transparent px-3 text-obsidian-500': true,
        }"
      >
        {{ adornment }}
      </div>
    </div>
    <InputHint :hint="hint" :is-error="isError" />
  </InputLabel>
</template>

<script setup lang="ts" generic="T extends string = 'text'">
import { nextTick, ref, watch } from 'vue';
import InputHint from './InputHint.vue';
import InputLabel from './InputLabel.vue';
import { useI18n } from 'vue-i18n';
import InputNumber, { type InputNumberInputEvent } from 'primevue/inputnumber';
import { input as inputCva } from '@carvia/theme/lib/cva/input';

type ValueType = T extends 'number' ? number : string;

const { locale } = useI18n();

const props = withDefaults(
  defineProps<{
    label?: string;
    placeholder?: string;
    hint?: string;
    modelValue?: ValueType | null;
    adornment?: string;
    adornmentTransparent?: boolean;
    isError?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    height?: string;
    warning?: string;
    tooltip?: string;
    class?: string;
    minFractionDigits?: number;
    maxFractionDigits?: number;
    useGrouping?: boolean;
    type?: T;
    min?: number;
    max?: number;
    emptyValueTo?: 'null' | 'undefined';
    variant?: 'default' | 'ghost' | 'inline';
    size?: 'sm' | 'default' | 'lg';
  }>(),
  {
    size: 'sm',
    useGrouping: true,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ValueType | null | undefined): void;
  (e: 'input', event: InputEvent | InputNumberInputEvent): void;
}>();

const input = ref<HTMLInputElement>();
const blur = () => {
  input.value?.blur();
};

defineExpose({
  blur,
  input,
});

const numberValue = ref(modelValueToNumberOrNull(props.modelValue));

const numberFieldOutputValue = ref<string | null>(
  typeof numberValue.value === 'number' ? numberValue.value?.toString() : null,
);

const maxFractionDigits = props.maxFractionDigits ? props.maxFractionDigits : 0;
// Enforce inputMode decimal - https://github.com/primefaces/primevue/issues/6629
const minFractionDigits = props.minFractionDigits ?? (maxFractionDigits ? 0.001 : 0);

const getInputNumberValue = (value: string | number | null | undefined) => {
  numberFieldOutputValue.value = value === undefined || value === null ? null : value.toString();
  emit('update:modelValue', stringOutputToNumberOrNull(numberFieldOutputValue.value) as ValueType);
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.type !== 'number') return;
    if (
      !isNaN(Number(numberFieldOutputValue.value ?? 0)) ||
      newValue !== stringOutputToNumberOrNull(numberFieldOutputValue.value)
    ) {
      numberValue.value = modelValueToNumberOrNull(newValue);
    }
  },
);

function stringOutputToNumberOrNull(value: string | null) {
  const emptyValue = props.emptyValueTo === 'undefined' ? undefined : null;
  return value !== null && !isNaN(Number(value)) ? Number(value) : emptyValue;
}

function modelValueToNumberOrNull(value: string | number | null | undefined) {
  const emptyValue = props.emptyValueTo === 'undefined' ? undefined : null;
  return value !== undefined && value !== null
    ? !isNaN(Number(value))
      ? Number(value)
      : 0
    : emptyValue;
}
</script>
